var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import { isFakeMouse } from '@hooks/touch/touchRules';
import classNames from 'classnames';
import useDimensions from 'react-cool-dimensions';
import { ResizeObserver } from '@juggle/resize-observer';
import { UXPContext } from '@core/context/Context';
import { blobToDataUrl, inferMediaKind, validateAllowedFileType } from './fileHelper';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import Input from '../Input';
import IconButton from '@widget/buttons/IconButton';
// Loaded only when a field asks to review its files
const FileReviewDialog = React.lazy(() => import('../MediaPicker/FileReviewDialog'));
/**
 * @export
 * FileInput component - Drag and drop file upload with preview
 * Follows v5 input pattern with theme support and accessibility
 *
 * @category Inputs/Pickers
 */
const FileInput = React.forwardRef((props, ref) => {
    const context = React.useContext(UXPContext);
    // Phones can't drag: the default label asks for a tap there
    const isMobile = useIsMobileLayout();
    const defaultLabel = context.$L(isMobile ? 'uxp-core.text.tap-to-choose' : 'uxp-core.text.drag-n-drop');
    let { value, onChange, multiple, onFilesChange, allowedTypes, maxSizeMB, preview, className, dropAreaIcon, dropAreaLabel, dropzone, dropAreaHint, minWidth, maxWidth, width, fullWidth, height, minHeight, maxHeight, disabled, readOnly, isValid: isValidProp, compact, hidden, allowUrl, urlPlaceholder, onUrlChange, suffix, hidePrefixIcon, onFieldClick, previewObjectFit = 'contain', removable, previewInfo, review } = props;
    // Every full-size field has the drop-zone look; `dropzone` is kept for existing callers
    const isDropzone = !compact;
    if (!preview)
        preview = { showPreview: true, showName: false };
    if (preview.showPreview == undefined)
        preview.showPreview = true;
    if (preview.showName == undefined)
        preview.showName = false;
    const [previewDetails, setPreviewDetails] = React.useState(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const [internalValidationError, setInternalValidationError] = React.useState(false);
    // Files waiting in the review dialog (`review`)
    const [reviewFiles, setReviewFiles] = React.useState(null);
    // Why the last picked or dropped file was refused (shown in the drop-zone)
    const [rejectReason, setRejectReason] = React.useState(null);
    const inputRef = React.useRef(null);
    const containerRef = React.useRef(null);
    // Short fields (under 9rem) drop the bar under the chosen file, so the picture keeps the room
    const [fieldHeight, setFieldHeight] = React.useState(0);
    const { observe } = useDimensions({
        useBorderBoxSize: true,
        polyfill: ResizeObserver,
        onResize: ({ height: h }) => setFieldHeight(h),
    });
    const isShort = fieldHeight > 0 && fieldHeight < 144;
    // Filter the OS file picker to the allowed set (MIME types and extensions both work here)
    const acceptAttr = (allowedTypes === null || allowedTypes === void 0 ? void 0 : allowedTypes.length) ? allowedTypes.join(',') : undefined;
    React.useImperativeHandle(ref, () => ({
        open: () => {
            var _a;
            if (!disabled && !readOnly)
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
        },
    }), [disabled, readOnly]);
    React.useEffect(() => {
        processValue();
    }, [value]);
    React.useEffect(() => {
    }, [previewDetails]);
    function processValue() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!value) {
                setPreviewDetails(null);
                return;
            }
            // String value = a URL. Render it directly as the preview source (works for
            // any origin — fetching to a blob CORS-fails for cross-origin URLs). Infer the
            // media type from the extension so images/videos preview correctly.
            if (typeof value == 'string') {
                const kind = inferMediaKind(value);
                // no dedicated audio preview here — it falls in with other files
                const type = kind === 'image' ? 'image' : kind === 'video' ? 'video' : 'other';
                setPreviewDetails({
                    isValid: true,
                    type,
                    mime: '',
                    name: value,
                    dataUrl: value
                });
                return;
            }
            const file = value;
            const isAllowed = validateAllowedFileType(file.type, allowedTypes || [], file.name);
            const dataUrl = yield blobToDataUrl(file);
            setPreviewDetails({
                isValid: isAllowed,
                type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'other',
                mime: file.type,
                dataUrl: dataUrl,
                name: file.name
            });
        });
    }
    function renderPreview() {
        return renderDropzonePreview();
    }
    /**
     * The drop-zone look: the badge and label when empty ("Release to upload" while a file is over it,
     * and why the last file was refused), and the chosen file on a plain stage with a bar under it.
     */
    function renderDropzonePreview() {
        if (previewDetails === null) {
            return (_jsx("div", { className: "uxpcore_fileinput__preview-container", children: _jsxs("div", { className: "uxpcore_fileinput__preview uxpcore_fileinput__preview--empty", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: dropAreaIcon ? dropAreaIcon : ['fas', 'arrow-up-from-bracket'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: isDragging
                                ? context.$L('uxp-core.text.release-to-upload')
                                : (dropAreaLabel ? dropAreaLabel : defaultLabel) }), dropAreaHint && !isDragging && _jsx("div", { className: "uxpcore_fileinput__hint", children: dropAreaHint }), rejectReason && !isDragging && _jsxs("div", { className: "uxpcore_fileinput__reject", children: [_jsx(FontAwesomeIconWrapper, { icon: ['fal', 'circle-exclamation'] }), _jsx("span", { children: rejectReason })] })] }) }));
        }
        if (previewDetails.isValid === false) {
            return (_jsx("div", { className: "uxpcore_fileinput__preview-container", children: _jsx("div", { className: "uxpcore_fileinput__preview", children: _jsxs("div", { className: "uxpcore_fileinput__invalid", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times-circle'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: context.$L('uxp-core.form-error.invalid-file') })] }) }) }));
        }
        const replaceHint = context.$L(multiple ? 'uxp-core.media-picker.drop-to-add' : 'uxp-core.text.drop-to-replace');
        return (_jsxs("div", { className: "uxpcore_fileinput__stage", children: [_jsx("div", { className: "uxpcore_fileinput__stage-media", children: previewDetails.type === 'image' ? (_jsx("img", { src: previewDetails.dataUrl, className: "uxpcore_fileinput__image", alt: previewDetails.name, style: { objectFit: previewObjectFit } })) : previewDetails.type === 'video' ? (_jsx("video", { src: previewDetails.dataUrl, className: "uxpcore_fileinput__video", style: { objectFit: previewObjectFit } })) : (_jsxs("div", { className: "uxpcore_fileinput__file", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'file-alt'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: previewDetails.name })] })) }), !isShort && (previewInfo !== undefined || previewDetails.type !== 'other') && (_jsx("div", { className: "uxpcore_fileinput__stage-name", children: previewInfo !== undefined ? previewInfo : previewDetails.name })), ((isHovered && !isMobile) || isDragging) && !disabled && !readOnly && (_jsxs("div", { className: "uxpcore_fileinput__replace", children: [_jsx(FontAwesomeIconWrapper, { icon: ['fal', 'arrow-up-from-bracket'] }), _jsx("span", { children: replaceHint })] })), removable && !disabled && !readOnly && (_jsx("span", { className: "uxpcore_fileinput__remove", onClick: (e) => e.stopPropagation(), children: _jsx(IconButton, { icon: "fal trash-can", tooltip: context.$L('uxp-core.text.remove'), variant: "danger", mode: "filled", size: "medium", onClick: () => { setPreviewDetails(null); onChange(null, false); } }) }))] }));
    }
    /** Why a file is refused, or null when it is allowed */
    function rejectionOf(file) {
        if (!validateAllowedFileType(file.type, allowedTypes || [], file.name))
            return context.$L('uxp-core.form-error.invalid-file');
        if (maxSizeMB > 0 && file.size > maxSizeMB * 1024 * 1024)
            return context.$L('uxp-core.form-error.file-too-large', { maxSizeMB });
        return null;
    }
    /** The one validation step, shared by the single- and multi-file paths. */
    function isFileAllowed(file) {
        return !rejectionOf(file);
    }
    function applyFile(file) {
        if (!file) {
            setInternalValidationError(false);
            setRejectReason(null);
            onChange(null, false);
            return;
        }
        const reason = rejectionOf(file);
        setRejectReason(reason);
        if (reason) {
            setInternalValidationError(true);
            onChange(null, false);
            return;
        }
        setInternalValidationError(false);
        onChange(file, true);
    }
    function applyFiles(files) {
        var _a;
        // `multiple` without a handler for it still behaves like a single field.
        if (!onFilesChange) {
            applyFile((_a = files[0]) !== null && _a !== void 0 ? _a : null);
            return;
        }
        const allValid = files.length > 0 && files.every(isFileAllowed);
        setInternalValidationError(files.length > 0 && !allValid);
        setRejectReason(files.map(rejectionOf).find(Boolean) || null);
        onFilesChange(files, allValid);
    }
    /** Routes a picked/dropped set through the single or the multi path. */
    function applySelection(files) {
        var _a;
        // Review first: the dialog hands back the files that passed
        if (review && files.length) {
            const allowed = files.filter(isFileAllowed);
            setRejectReason(allowed.length < files.length ? files.map(rejectionOf).find(Boolean) : null);
            if (allowed.length)
                setReviewFiles(multiple ? allowed : allowed.slice(0, 1));
            return;
        }
        if (multiple)
            applyFiles(files);
        else
            applyFile((_a = files[0]) !== null && _a !== void 0 ? _a : null);
    }
    /** The files the review let through */
    function takeReviewed(files) {
        setReviewFiles(null);
        if (!files.length)
            return;
        if (multiple && onFilesChange)
            onFilesChange(files, true);
        else
            applyFile(files[0]);
    }
    const reviewDialog = review && reviewFiles && _jsx(React.Suspense, { fallback: null, children: _jsx(FileReviewDialog, { show: true, files: reviewFiles, title: review.title, allowedTypes: allowedTypes, validate: review.validate, cropRules: review.cropRules, onUse: takeReviewed, onClose: () => setReviewFiles(null) }) });
    function handleFileChange(e) {
        if (disabled || readOnly)
            return;
        const files = Array.from(e.target.files || []);
        // Reset so selecting the same file again re-fires onChange
        // (component state is driven by the `value` prop, not the input)
        e.target.value = '';
        applySelection(files);
    }
    function handleDragOver(e) {
        if (disabled || readOnly)
            return;
        e.preventDefault();
        e.stopPropagation();
    }
    function handleDragEnter(e) {
        if (disabled || readOnly)
            return;
        e.preventDefault();
        setIsDragging(true);
    }
    function handleDragLeave(e) {
        var _a;
        if (disabled || readOnly)
            return;
        // Only clear when leaving the container itself, not a child element
        if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
            setIsDragging(false);
        }
    }
    function handleDrop(e) {
        if (disabled || readOnly)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        applySelection(Array.from(e.dataTransfer.files || []));
    }
    function handleContainerClick() {
        var _a;
        if (disabled || readOnly)
            return;
        // A consumer that owns the click still gets drops — only the OS picker
        // is suppressed.
        if (onFieldClick)
            onFieldClick();
        else
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }
    function handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleContainerClick();
        }
    }
    function renderCompact() {
        var _a, _b;
        const icon = dropAreaIcon !== null && dropAreaIcon !== void 0 ? dropAreaIcon : ['fal', 'link'];
        const label = (_b = (_a = previewDetails === null || previewDetails === void 0 ? void 0 : previewDetails.name) !== null && _a !== void 0 ? _a : (typeof value === 'string' && value ? value : null)) !== null && _b !== void 0 ? _b : (dropAreaLabel !== null && dropAreaLabel !== void 0 ? dropAreaLabel : defaultLabel);
        return (_jsxs(_Fragment, { children: [_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, multiple: multiple, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }), !hidePrefixIcon &&
                    _jsx("div", { className: "uxpcore_input__prefix uxpcore_input__prefix--separator", children: _jsx("div", { className: "uxpcore_input__slot-content", children: _jsx(FontAwesomeIconWrapper, { icon: icon }) }) }), _jsx("div", { className: classNames('uxpcore_fileinput__compact-label', {
                        'uxpcore_fileinput__compact-label--placeholder': !previewDetails,
                    }), children: label }), previewDetails && !disabled && !readOnly && (_jsx("button", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); setPreviewDetails(null); onChange(null, false); }, tabIndex: -1, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'times'] }) })), suffix && (
                // The slot is an action area, not part of the click-to-pick
                // surface — without this the container handler also fires
                // and opens the OS file dialog on top of whatever it does.
                _jsx("div", { className: "uxpcore_input__suffix uxpcore_input__suffix--separator", onClick: (e) => e.stopPropagation(), children: _jsx("div", { className: "uxpcore_input__slot-content", children: suffix }) }))] }));
    }
    function handleFocus() {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    }
    function handleBlur() {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    }
    // Width sizes the outer control; height sizes the drop-zone. In allowUrl mode these
    // land on different elements (wrapper vs inner drop-zone), so keep them separate.
    const widthStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const heightStyles = getInputSizeStyles({ height, minHeight, maxHeight, style: undefined });
    const containerStyles = Object.assign(Object.assign({}, widthStyles), heightStyles);
    // Parent's isValid prop takes priority.
    // Without it: show invalid when a file was rejected, valid when a file is accepted,
    // and neutral (undefined) when nothing is selected yet.
    const effectiveValidation = isValidProp !== undefined
        ? isValidProp
        : internalValidationError
            ? false
            : previewDetails !== null
                ? true
                : undefined;
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: effectiveValidation });
    // Headless mode: only the (invisible) native input; the consumer supplies
    // the trigger UI and calls open() on the instance ref.
    if (hidden) {
        return (_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, multiple: multiple, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }));
    }
    const containerJsx = (_jsx("div", { ref: (el) => { containerRef.current = el; observe(el); }, className: classNames("uxpcore_input", "uxpcore_fileinput", className, stateClass, {
            // In allowUrl mode the size styles live on the outer wrapper, so the
            // drop-zone fills that constrained width instead of sizing itself.
            "uxpcore_input--full-width": fullWidth || allowUrl,
            "uxpcore_input--focus": isFocused,
            "uxpcore_input--has-content": !!previewDetails,
            "uxpcore_fileinput--hovered": isHovered && !disabled && !readOnly,
            "uxpcore_fileinput--dragging": isDragging,
            "uxpcore_fileinput--compact": compact,
            "uxpcore_fileinput--dropzone": isDropzone,
            "uxpcore_fileinput--short": isDropzone && isShort,
            "uxpcore_input--has-prefix": compact && !hidePrefixIcon,
        }), onClick: handleContainerClick, onKeyDown: handleKeyDown, onFocus: handleFocus, onBlur: handleBlur, 
        // A tap's fake hover would leave the drop overlay stuck on
        onMouseEnter: () => { if (!isFakeMouse())
            setIsHovered(true); }, onMouseLeave: () => setIsHovered(false), onDragOver: handleDragOver, onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDrop: handleDrop, tabIndex: disabled || readOnly ? -1 : 0, style: allowUrl ? heightStyles : containerStyles, children: compact ? renderCompact() : (_jsxs(_Fragment, { children: [_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, multiple: multiple, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }), _jsx("div", { className: "uxpcore_fileinput__content", children: renderPreview() })] })) }));
    if (!allowUrl)
        return _jsxs(_Fragment, { children: [containerJsx, reviewDialog] });
    // The URL row is a sibling of the drop-zone (not a child) so clicking/typing in it
    // does not bubble to the drop-zone's click-to-open handler.
    return (_jsxs("div", { className: classNames("uxpcore_fileinput__with-url", { "uxpcore_input--full-width": fullWidth }), style: widthStyles, children: [containerJsx, reviewDialog, _jsxs("div", { className: "uxpcore_fileinput__url-row", children: [_jsx("span", { className: "uxpcore_fileinput__url-sep", children: context.$L('uxp-core.text.or-enter-url') }), _jsx(Input, { value: typeof value === 'string' ? value : '', onChange: (v) => onUrlChange === null || onUrlChange === void 0 ? void 0 : onUrlChange(v), placeholder: urlPlaceholder, disabled: disabled, readOnly: readOnly, fullWidth: true })] })] }));
});
FileInput.displayName = 'FileInput';
export default FileInput;
